import Flex from 'UIKit/Box/Flex'
import { BoxProps } from 'UIKit/Box/types'
import Text from 'UIKit/Text'
import { TokenAmount } from 'config/types'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from 'state'
import { useAuth } from 'state/auth/hooks'
import styled, { css } from 'styled-components'

import { StyledMenuDropdownWrapper } from 'UIKit/Popper/Popper'
import { useGetDisplayAmount } from 'state/profile/hooks'
import { getFullDisplayBalance } from 'utils/formatBalance'
import { useMaxActiveReward } from 'views/Payment/components/Deposit/DepositCryptos/hooks'
import { DepositRewardEnum } from 'views/Payment/constants'
import { getDepositBonusReward } from 'views/Payment/utils'
import PaymentButton from '../PaymentButton'
import { RowMiddle } from '../Row'
import BaseBalanceSelect from './components/BaseBalanceSelect'
import { TokenAmountLayout } from './components/TokenAmountOptionItem'
import { useSelectedBalance } from './hooks'

const StyledWalletIconButton = styled(Flex)`
  position: absolute;
  width: 44px;
  height: 44px;
  top: 0px;
  right: 0px;
  border-radius: 50%;
  justify-content: center;
  align-items: center;
  display: none;

  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.04);
`

const StyledBalanceSelect = styled(BaseBalanceSelect)`
  height: 44px;
  border-top-left-radius: ${({ theme }) => theme.radii.small};
  border-bottom-left-radius: ${({ theme }) => theme.radii.small};

  padding: 0px 12px;
  width: 100%;

  ${({ theme }) => theme.mediaQueries.sm} {
    width: 250px;
  }
`

const StyledDepositWrapper = styled(Flex)`
  height: 44px;
  border-top-right-radius: ${({ theme }) => theme.radii.small};
  border-bottom-right-radius: ${({ theme }) => theme.radii.small};
  padding-right: 4px;
  align-items: center;

  ${StyledWalletIconButton} {
    display: flex;
  }

  ${({ theme }) => theme.mediaQueries.xxxl} {
    ${StyledWalletIconButton} {
      display: none;
    }
  }
`

const StyledContainer = styled(RowMiddle)<{ $active: boolean }>`
  ${({ $active }) =>
    $active &&
    css`
      background: ${({ theme }) => theme.colors.card_primary};
      backdrop-filter: blur(15px);
      border-radius: ${({ theme }) => theme.radii.modal};
    `}

  ${({ theme }) => theme.mediaQueries.md} {
    ${StyledMenuDropdownWrapper} {
      width: auto;
    }
  }
`

const StyledPaymentButton = styled(PaymentButton)<{ badgeBonus: string }>`
  display: flex;
  white-space: nowrap;

  position: relative;

  button {
    min-width: 88px;
    z-index: 1;
    transform-origin: center 100%;
  }

  button:hover {
    transition: 0.1s ease-in-out 0s;
    transform: scale(1.025);
  }

  ${({ badgeBonus, theme }) =>
    badgeBonus &&
    css`
      &::before {
        content: ' ';
        position: absolute;
        top: 50%;
        left: 50%;

        width: 111%;
        height: 124%;
        border-radius: ${({ theme }) => theme.radii.modal};
        transform: translate(-50%, -50.5%);

        border: 3px solid ${theme.colors.warning};
        z-index: 0;
      }

      &::after {
        content: '${badgeBonus}';
        position: absolute;
        top: 0;
        right: 0;
        transform: translate(25%, -65%);
        padding: 3px 4px;
        border-radius: ${({ theme }) => theme.radii.button};
        background-color: ${theme.colors.warning};
        color: #fff;
        font-size: 12px;
        font-weight: bold;
        z-index: 1;
        min-width: 30px;
        text-align: center;
      }
    `}
`

const TokenAmountItem: React.FC<{ amount: TokenAmount }> = ({ amount }) => {
  const { displayAmount, symbol, selectedFiat, isDisplayInFiat } = useGetDisplayAmount(amount)

  return (
    <TokenAmountLayout token={amount.token} hideTokenNameOnMobile>
      <Flex height="20px" alignItems="center">
        <Text fontSize="14px" fontWeight={700} ml="6px">
          {symbol}
        </Text>
        <Text color="text" fontSize="14px" fontWeight={700}>
          {getFullDisplayBalance(Number(displayAmount), 0, isDisplayInFiat ? selectedFiat.displayDecimals : 6)}
        </Text>
      </Flex>
    </TokenAmountLayout>
  )
}

export interface BalanceSelectProps {
  enableSetting?: boolean
}

const BalanceSelect: React.FC<BoxProps & BalanceSelectProps> = ({ enableSetting = true, ...props }) => {
  const { hasSession } = useAuth()
  const { selectedBalance } = useSelectedBalance()
  const { handleSelectBalance } = useSelectedBalance()
  const { t } = useTranslation()
  const isInGame = useAppSelector((state) => state.app.isInGame)

  const bonus = useMaxActiveReward()

  const valueDisplay = useMemo(() => {
    const { value, type } = getDepositBonusReward(bonus)

    if (type === DepositRewardEnum.Percent) {
      return `+${(value as number).toFixed(0)}%`
    }
    if (type === DepositRewardEnum.Cash && (value as TokenAmount)?.token) {
      return `+${getFullDisplayBalance((value as TokenAmount)?.amount, 0, 2)} ${
        (value as TokenAmount)?.token?.code || ''
      }`
    }
    if (type === DepositRewardEnum.FreeSpin) {
      return `+${(value as number).toFixed(0)} FS`
    }
    if (type == DepositRewardEnum.Cashback) {
      return `+${(value as number).toFixed(0)}% CB`
    }
    return ''
  }, [bonus])

  return (
    <StyledContainer $active={hasSession} id="balance-select" {...props}>
      {hasSession && (
        <>
          <StyledBalanceSelect
            handleSelectBalance={handleSelectBalance}
            selectedToken={selectedBalance.token}
            enableSetting={enableSetting}
          >
            {selectedBalance &&
              (isInGame ? (
                <TokenAmountLayout token={selectedBalance.token} hideTokenNameOnMobile>
                  <Text fontSize="14px" fontWeight={700} color="text" ml="10px" mt="3px">
                    {t('In Game')}
                  </Text>
                </TokenAmountLayout>
              ) : (
                <TokenAmountItem amount={selectedBalance} />
              ))}
          </StyledBalanceSelect>

          <StyledDepositWrapper>
            <StyledPaymentButton badgeBonus={valueDisplay} id="payment-button" />
          </StyledDepositWrapper>
        </>
      )}
    </StyledContainer>
  )
}

export default BalanceSelect
